/* Drop-down panels under the site header: the site menu and the bag drawer.
   A panel is a [popover="manual"][data-site-panel] inside .site-header; anything with
   data-panel-open="<panel id>" opens/closes it (links keep their href as the no-JS fallback).
   - One panel open at a time; opening one closes the other.
   - Same slide both ways: drops down from under the header, close is the exact reverse.
     Web Animations, because CSS exit transitions don't run in Safari.
   - Focus moves into the panel, Tab stays inside it (plus its header trigger), Esc closes
     and focus goes back to whatever opened it. prefers-reduced-motion: no slide.
   Public: window.saysoPanels.open(id, trigger), .close(id, { restoreFocus, quiet }), .isOpen(id).
   quiet: no close sound/haptic (detail.quiet), for a close nobody sees (under the page overlay). Events on the panel:
   "site-panel:open" / "site-panel:close". */
(() => {
  if (window.saysoPanels) return;

  const DURATION = 400;
  const EASING = "cubic-bezier(0.65, 0, 0.35, 1)";
  // Slide and clip move together, so the panel never paints over the header while it moves.
  const UP = { transform: "translateY(-100%)", clipPath: "inset(100% 0 0 0)" };
  const DOWN = { transform: "translateY(0)", clipPath: "inset(0 0 0 0)" };
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const anims = new WeakMap();
  const returnFocus = new WeakMap();
  let current = null;

  const byId = (id) => {
    const el = typeof id === "string" ? document.getElementById(id) : id;
    return el && el.hasAttribute("data-site-panel") ? el : null;
  };
  const triggersFor = (panel) => document.querySelectorAll(`[data-panel-open="${panel.id}"]`);
  const isShown = (panel) => panel.matches(":popover-open");
  const isOpen = (panel) => isShown(panel) && panel.dataset.state !== "closing";
  const setExpanded = (panel, open) =>
    triggersFor(panel).forEach((t) => t.setAttribute("aria-expanded", String(open)));

  // Cover the header's column, from its bottom edge to the bottom of the screen.
  const place = (panel) => {
    const header = panel.closest(".site-header") || document.querySelector(".site-header");
    if (!header) return;
    const r = header.getBoundingClientRect();
    const top = Math.max(r.bottom, 0);
    Object.assign(panel.style, {
      top: `${top}px`,
      left: `${r.left}px`,
      width: `${r.width}px`,
      height: `${window.innerHeight - top}px`,
    });
  };

  const slide = (panel, from, to) => {
    const prev = anims.get(panel);
    const progress = prev && prev.playState === "running" ? prev.currentTime : null;
    if (prev) prev.cancel();
    const duration = reducedMotion.matches ? 0 : DURATION;
    const anim = panel.animate([from, to], { duration, easing: EASING, fill: "forwards" });
    if (progress != null && duration) anim.currentTime = Math.max(0, duration - progress); // reverse from where it was
    anims.set(panel, anim);
    return anim.finished;
  };

  const visible = (el) => el.getClientRects().length > 0 && !el.closest("[hidden]");
  const focusables = (panel) => {
    const own = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(visible);
    const header = panel.closest(".site-header");
    const headerTriggers = Array.from(triggersFor(panel)).filter((t) => header && header.contains(t));
    return [...headerTriggers, ...own];
  };

  const hideNow = (panel) => {
    anims.get(panel)?.cancel();
    if (isShown(panel)) panel.hidePopover();
    panel.dataset.state = "";
    setExpanded(panel, false);
    if (current === panel) current = null;
  };

  const open = (id, trigger = null) => {
    const panel = byId(id);
    if (!panel) return false;
    if (current && current !== panel) hideNow(current);
    if (trigger) returnFocus.set(panel, trigger);
    place(panel);
    if (!isShown(panel)) panel.showPopover();
    panel.dataset.state = "open";
    current = panel;
    setExpanded(panel, true);
    slide(panel, UP, DOWN).catch(() => {});
    const first = panel.querySelector("[data-panel-autofocus]") || panel;
    first.focus({ preventScroll: true });
    panel.dispatchEvent(new CustomEvent("site-panel:open", { detail: { trigger } }));
    return true;
  };

  const close = (id, { restoreFocus = true, quiet = false } = {}) => {
    const panel = byId(id);
    if (!panel || !isOpen(panel)) return;
    panel.dataset.state = "closing";
    setExpanded(panel, false);
    if (current === panel) current = null;
    const back = returnFocus.get(panel);
    if (restoreFocus && back && panel.contains(document.activeElement)) back.focus({ preventScroll: true });
    panel.dispatchEvent(new CustomEvent("site-panel:close", { detail: { quiet } }));
    slide(panel, DOWN, UP).then(
      () => {
        if (panel.dataset.state === "closing") hideNow(panel);
      },
      () => {},
    );
  };

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-panel-open]");
    if (!trigger || event.defaultPrevented) return;
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // new tab etc.
    const panel = byId(trigger.dataset.panelOpen);
    if (!panel) return;
    event.preventDefault(); // take over the native popover toggle / the link
    if (isOpen(panel)) close(panel);
    else open(panel, trigger);
  });

  document.addEventListener("keydown", (event) => {
    if (!current) return;
    if (event.key === "Escape") {
      event.preventDefault();
      const panel = current;
      const back = returnFocus.get(panel);
      close(panel, { restoreFocus: false });
      back?.focus({ preventScroll: true });
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusables(current);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!items.includes(active) && !current.contains(active)) return; // focus is elsewhere, leave it
    if (event.shiftKey && (active === first || active === current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Click or tap outside closes (header triggers excluded so they can switch panels).
  document.addEventListener(
    "pointerdown",
    (event) => {
      if (!current) return;
      if (current.contains(event.target) || event.target.closest("[data-panel-open]")) return;
      close(current, { restoreFocus: false });
    },
    true,
  );

  window.addEventListener("resize", () => current && place(current), { passive: true });
  // Back/forward cache: never come back to a half-open panel.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) document.querySelectorAll("[data-site-panel]").forEach(hideNow);
  });

  window.saysoPanels = {
    open,
    close,
    isOpen: (id) => Boolean(byId(id) && isOpen(byId(id))),
  };
})();
